import { Coordinate, TileSize } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import type { TwoStoreyHouse } from "game-common/room/ember/two_storey_house"

/**
 * WHICH PART OF A STOREY TO CUT AWAY over a player standing under it. Every building of a room shares
 * one stack of storey rooms, so a storey can hold several separate structures: a castle's four tower
 * tops, two neighbours' upper floors. Going inside one used to hide the whole storey -- every other
 * tower top, the house next door. Now only the structures standing over the covered area the player is
 * in are cut away; one that reaches nowhere near it stays drawn. A storey that is all one structure
 * (any ordinary house) is still cut away whole, exactly as before.
 */

const key = (x: number, y: number) => `${x},${y}`

const component = (cells: ReadonlySet<string>, start: Coordinate): Set<string> => {
    const seen = new Set<string>()
    const queue = [start]
    for (let i = 0; i < queue.length; i++) {
        const { x, y } = queue[i]
        const id = key(x, y)
        if (seen.has(id) || !cells.has(id)) continue
        seen.add(id)
        queue.push({ x: x + 1, y }, { x: x - 1, y }, { x, y: y + 1 }, { x, y: y - 1 })
    }
    return seen
}

const coverCache = new WeakMap<readonly Coordinate[], { cells: Set<string>; parts: Map<string, Set<string>> }>()

/**
 * The covered area the player's soles stand in: the cells of `house.interiorCells` (everything with a
 * roof or a higher floor over it) joined to the player's own. Undefined when nothing covers them.
 */
export const coverAt = (house: TwoStoreyHouse, at: Coordinate): ReadonlySet<string> | undefined => {
    const interior = house.interiorCells
    if (!interior?.length) return undefined
    let cached = coverCache.get(interior)
    if (!cached) coverCache.set(interior, cached = { cells: new Set(interior.map(cell => key(cell.x, cell.y))), parts: new Map() })
    const feet = { x: Math.floor(at.x / TileSize), y: Math.floor((at.y + BODY_GROUND_OFFSET) / TileSize) }
    const id = key(feet.x, feet.y)
    if (!cached.cells.has(id)) return undefined
    for (const part of cached.parts.values()) if (part.has(id)) return part
    const part = component(cached.cells, feet)
    cached.parts.set(id, part)
    return part
}

/**
 * The structures of a storey (its footprint split into connected pieces) standing over `cover`.
 * `whole` when every piece does: then the storey is simply cut away, as it always was.
 */
export const structuresOver = (footprint: readonly Coordinate[], cover: ReadonlySet<string>):
    { cut: Set<string>; whole: boolean } => {
    const cells = new Set(footprint.map(cell => key(cell.x, cell.y)))
    const seen = new Set<string>()
    const cut = new Set<string>()
    let whole = true
    for (const cell of footprint) {
        if (seen.has(key(cell.x, cell.y))) continue
        const piece = component(cells, cell)
        piece.forEach(id => seen.add(id))
        if ([...piece].some(id => cover.has(id))) piece.forEach(id => cut.add(id))
        else whole = false
    }
    return { cut, whole }
}

/** How many cells above its own a cell's art reaches: walls stand up, roofs are lifted. */
export const CUT_ART_REACH = 2

export interface CutRun { x0: number; x1: number; y: number; alpha: number }

/**
 * The cut drawn as a mask over the storey's cells `[left, right) x [top, bottom)`: opaque, except the cut
 * cells and the art they reach up into, at `alpha`. One run per stretch of a row with the same alpha.
 */
export const cutMaskRuns = (cut: ReadonlySet<string>, alpha: number,
    left: number, top: number, right: number, bottom: number): CutRun[] => {
    const runs: CutRun[] = []
    for (let y = top; y < bottom; y++) {
        let start = left, current: number | undefined
        for (let x = left; x <= right; x++) {
            let value: number | undefined
            if (x < right) {
                value = 1
                for (let reach = 0; reach <= CUT_ART_REACH; reach++) if (cut.has(key(x, y + reach))) { value = alpha; break }
            }
            if (value !== current) {
                if (current !== undefined) runs.push({ x0: start, x1: x, y, alpha: current })
                start = x
                current = value
            }
        }
    }
    return runs
}
